/**
 * Braço do A/B de fluxo (flow-ab-2026-08) no cliente.
 *
 * O sorteio é do backend (functions/lib/flowAb.js), feito quando o CPF entra
 * no funil. Aqui só guardamos o resultado para a triagem e o chat saberem qual
 * fluxo seguir sem uma ida ao servidor a cada tela.
 *
 * O valor fica amarrado ao patientId (mesmo padrão de `anamneseProntuarioFor`):
 * sem isso, um segundo cadastro no mesmo navegador herdaria o braço do
 * anterior e contaminaria o experimento.
 */

const KEY = 'flowAbVariant';
const KEY_FOR = 'flowAbVariantFor';
const KEY_CONDUCT_PRE_PAY = 'flowAbConductDeliveredFor';

// Exportadas porque o gate anti-bypass de triagem
// (`consultationSessionBound.hasLegitimateChatAccessForPatient`) precisa ler o
// braço com o mesmo leitor injetado que usa para as outras chaves, em vez de
// repetir os nomes como literais.
export const FLOW_VARIANT_KEY = KEY;
export const FLOW_VARIANT_FOR_KEY = KEY_FOR;

export const FLOW_CONTROL = 'control';
export const FLOW_SHORT_PAID = 'short_paid';
export const FLOW_FULL_FREE_CONDUCT = 'full_free_conduct';
export const FLOW_SHORT_FREE_CONDUCT = 'short_free_conduct';
export const FLOW_NO_TRIAGE_PAID = 'no_triage_paid';

const VALID = [
  FLOW_CONTROL,
  FLOW_SHORT_PAID,
  FLOW_FULL_FREE_CONDUCT,
  FLOW_SHORT_FREE_CONDUCT,
  FLOW_NO_TRIAGE_PAID,
];

function currentPatientId() {
  try {
    return (
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      ''
    );
  } catch {
    return '';
  }
}

export function setFlowVariant(variant, patientId) {
  const v = String(variant || '').trim();
  if (!VALID.includes(v)) return;
  const pid = String(patientId || currentPatientId() || '');
  if (!pid) return;
  try {
    localStorage.setItem(KEY, v);
    localStorage.setItem(KEY_FOR, pid);
  } catch {}
}

/**
 * Braço atribuído, ou null quando o paciente está fora do experimento
 * (desligado, já foi cliente, sem CPF). A telemetria usa esta versão: na
 * análise, "fora do experimento" e "controle" não podem virar a mesma coisa.
 */
export function getFlowVariantRaw() {
  try {
    const pid = currentPatientId();
    if (!pid) return null;
    if (localStorage.getItem(KEY_FOR) !== pid) return null;
    const v = localStorage.getItem(KEY);
    return VALID.includes(v) ? v : null;
  } catch {
    return null;
  }
}

/** Braço do paciente atual. Sempre devolve algo utilizável: control por padrão. */
export function getFlowVariant() {
  return getFlowVariantRaw() || FLOW_CONTROL;
}

export function clearFlowVariant() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(KEY_FOR);
    localStorage.removeItem(KEY_CONDUCT_PRE_PAY);
  } catch {}
}

/** Triagem reduzida ao núcleo de segurança. */
export function isReducedTriage(variant = getFlowVariant()) {
  return variant === FLOW_SHORT_PAID || variant === FLOW_SHORT_FREE_CONDUCT;
}

/**
 * Não passa pela triagem: do cadastro vai direto ao chat, e a anamnese inteira
 * acontece lá depois do pagamento.
 *
 * Propositalmente fora de `isReducedTriage`: quem decide rota olha este
 * predicado, e quem decide a lista de campos da triagem olha o outro. Juntar os
 * dois faria a triagem reduzida renderizar para um braço que nem deveria
 * chegar nela.
 */
export function skipsTriage(variant = getFlowVariant()) {
  return variant === FLOW_NO_TRIAGE_PAID;
}

/** Recebe conduta médica antes de pagar. */
export function givesFreeConduct(variant = getFlowVariant()) {
  return variant === FLOW_FULL_FREE_CONDUCT || variant === FLOW_SHORT_FREE_CONDUCT;
}

/**
 * Marca que a conduta do médico já chegou ao paciente sem pagamento.
 *
 * Pertencer a um braço de conduta gratuita não basta para dizer que o
 * atendimento aconteceu: até o médico enviar, o paciente continua elegível ao
 * cancelamento self-service (`checkSelfRefundEligibility` só nega com
 * `conduct_sent`). O paywall precisa do fato, não do braço — e o fato nasce no
 * chat, mas é lido também em /pagamento.
 */
export function markConductDeliveredBeforePayment(patientId) {
  const pid = String(patientId || currentPatientId() || '');
  if (!pid) return;
  try {
    localStorage.setItem(KEY_CONDUCT_PRE_PAY, pid);
  } catch {}
}

export function hasConductDeliveredBeforePayment() {
  try {
    const pid = currentPatientId();
    if (!pid) return false;
    return localStorage.getItem(KEY_CONDUCT_PRE_PAY) === pid;
  } catch {
    return false;
  }
}
